import { useMemo } from "react";
import { Link } from "react-router-dom";
import { parseISO, differenceInDays, addDays, startOfDay, format, isValid } from "date-fns";
import { it } from "date-fns/locale";
import { trafficLight } from "../lib/format";

const DAY_WIDTH = 10;
const ROW_HEIGHT = 30;
const parseSafe = (iso) => { try { const d = parseISO(iso); return isValid(d) ? d : null; } catch { return null; } };

/**
 * Compact single-client Gantt used on ClientDetail.
 * Auto-scales the visible window from the earliest project date (or creation)
 * to the latest deadline, always including "today".
 */
export default function ClientMiniGantt({ projects }) {
  const { days, todayIdx, start, hasRange } = useMemo(() => {
    const all = [];
    projects.forEach((p) => {
      for (const iso of [p.deadline, p.created_at, ...(p.milestones || []).map((m) => m.due_date)]) {
        const d = parseSafe(iso);
        if (d) all.push(d);
      }
    });
    const now = startOfDay(new Date());
    let s = now, e = addDays(now, 30);
    if (all.length) {
      const min = new Date(Math.min(...all.map((d) => d.getTime())));
      const max = new Date(Math.max(...all.map((d) => d.getTime())));
      s = startOfDay(new Date(Math.min(min.getTime(), now.getTime()) - 5 * 86400000));
      e = startOfDay(new Date(Math.max(max.getTime(), now.getTime()) + 10 * 86400000));
    }
    const total = Math.max(20, differenceInDays(e, s) + 1);
    const arr = Array.from({ length: total }, (_, i) => addDays(s, i));
    const ti = differenceInDays(startOfDay(new Date()), s);
    return { days: arr, todayIdx: ti, start: s, hasRange: all.length > 0 };
  }, [projects]);

  const dayIndex = (iso) => {
    const d = parseSafe(iso);
    if (!d) return null;
    return differenceInDays(startOfDay(d), start);
  };

  const sorted = useMemo(() => [...projects].sort((a, b) => {
    const da = parseSafe(a.deadline)?.getTime() ?? Infinity;
    const db = parseSafe(b.deadline)?.getTime() ?? Infinity;
    return da - db;
  }), [projects]);

  if (!hasRange) {
    return <div className="p-6 text-center text-sm text-neutral-500 border border-dashed">Nessuna gara con date valide da visualizzare.</div>;
  }

  const monthMarkers = [];
  let lastMonth = -1;
  days.forEach((d, i) => {
    if (d.getMonth() !== lastMonth) { monthMarkers.push({ idx: i, date: d }); lastMonth = d.getMonth(); }
  });

  return (
    <div className="border border-neutral-200 bg-white overflow-hidden" data-testid="client-mini-gantt">
      <div className="flex">
        {/* Left labels */}
        <div className="w-56 flex-shrink-0 border-r border-neutral-200">
          <div className="h-10 border-b border-neutral-200 flex items-center px-3 mono-label bg-neutral-50">Gara</div>
          {sorted.map((p) => {
            const tl = trafficLight(p.deadline, p.delivered);
            return (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`client-gantt-row-${p.id}`}
                className="flex items-center gap-2 px-3 border-b border-neutral-100 hover:bg-neutral-50" style={{ height: ROW_HEIGHT }}>
                <div className={`h-1.5 w-1.5 tl-bar-${tl.level} flex-shrink-0`} />
                <div className="text-xs font-medium truncate flex-1">{p.title}</div>
              </Link>
            );
          })}
        </div>

        {/* Right panel */}
        <div className="flex-1 overflow-x-auto">
          <div style={{ width: days.length * DAY_WIDTH, minWidth: "100%" }}>
            <div className="h-10 border-b border-neutral-200 bg-neutral-50 relative">
              {monthMarkers.map((m) => (
                <div key={m.date.toISOString().slice(0, 7)} className="absolute top-0 bottom-0 flex items-center pl-1 border-l border-neutral-200 font-mono text-[9px] text-neutral-500 uppercase whitespace-nowrap"
                  style={{ left: m.idx * DAY_WIDTH }}>
                  {format(m.date, "MMM yy", { locale: it })}
                </div>
              ))}
              {todayIdx >= 0 && todayIdx < days.length && (
                <div className="absolute top-0 bottom-0 w-px bg-orange-500 z-10" style={{ left: todayIdx * DAY_WIDTH }} />
              )}
            </div>
            {sorted.map((p) => {
              const tl = trafficLight(p.deadline, p.delivered);
              const dIdx = dayIndex(p.deadline);
              const cIdx = dayIndex(p.created_at);
              const barStart = Math.max(0, cIdx ?? Math.max(0, (dIdx ?? 0) - 5));
              const barEnd = Math.min(days.length - 1, dIdx ?? barStart);
              const showBar = barEnd >= barStart;
              return (
                <div key={p.id} className="relative border-b border-neutral-100 hover:bg-neutral-50" style={{ height: ROW_HEIGHT }}>
                  {todayIdx >= 0 && todayIdx < days.length && (
                    <div className="absolute top-0 bottom-0 w-px bg-orange-300 z-10" style={{ left: todayIdx * DAY_WIDTH }} />
                  )}
                  {(p.milestones || []).map((m) => {
                    const mi = dayIndex(m.due_date);
                    if (mi == null || mi < 0 || mi >= days.length) return null;
                    return (
                      <div key={m.id}
                        title={`${m.name} · ${m.percentage}%`}
                        className={`absolute top-1/2 -translate-y-1/2 h-2 w-2 rotate-45 border ${m.completed ? "brand-bg brand-border" : "bg-white border-neutral-500"}`}
                        style={{ left: mi * DAY_WIDTH - 4 }} />
                    );
                  })}
                  {showBar && (
                    <div className={`absolute top-1/2 -translate-y-1/2 h-4 border font-mono text-[9px] font-semibold flex items-center justify-end pr-1 ${tl.className}`}
                      style={{ left: barStart * DAY_WIDTH, width: Math.max(DAY_WIDTH * 2, (barEnd - barStart + 1) * DAY_WIDTH) }}
                      title={p.title}>
                      <span className="truncate">{p.delivered ? "OK" : (dIdx != null ? format(parseISO(p.deadline), "d MMM", { locale: it }) : "")}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
